import { useAuth } from '@/app/providers/AuthContext';
import { qk } from '@/shared/api/keys';
import { supabase } from '@/shared/api/supabaseClient';
import { useSupabaseQuery } from '@/shared/api/useSupabaseQuery';
import { useCallback, useMemo } from 'react';

export type PermissionEntitlements = Record<string, { enabled: boolean; limit: number | null }>;

// Plan-based entitlements (subscription tier features)
async function fetchPermissionEntitlements(): Promise<PermissionEntitlements> {
  const { data, error } = await supabase.rpc('get_my_permission_entitlements');
  if (error) throw error;
  return (data ?? {}) as PermissionEntitlements;
}

// Role-based permissions (admin capabilities)
async function fetchRolePermissions(): Promise<Set<string>> {
  const { data, error } = await supabase.rpc('get_my_role_permissions');
  if (error) throw error;
  const perms = (data ?? {}) as Record<string, boolean>;
  return new Set(Object.keys(perms));
}

export function usePermissionEntitlements() {
  const { data, isLoading, error } = useSupabaseQuery(
    qk.list('permission-entitlements'),
    async () => fetchPermissionEntitlements(),
    {
      namespaces: ['ui', 'auth'],
      staleTime: 5 * 60 * 1000, // cache for five minutes; entitlements rarely change mid-session
    }
  );

  const enabledPerms = useMemo(() => {
    const set = new Set<string>();
    if (data) {
      for (const [perm, info] of Object.entries(data)) {
        if (info?.enabled) set.add(perm);
      }
    }
    return set;
  }, [data]);

  const has = useCallback(
    (permissionCode: string) => enabledPerms.has(permissionCode),
    [enabledPerms],
  );

  return {
    loading: isLoading,
    error,
    has,
    enabledPerms,
  } as const;
}

export function useRolePermissions() {
  const { data, isLoading, error } = useSupabaseQuery(
    qk.list('role-permissions'),
    async () => fetchRolePermissions(),
    {
      namespaces: ['ui', 'auth'],
      staleTime: 5 * 60 * 1000, // cache for five minutes; role permissions rarely change mid-session
    }
  );

  const has = useCallback(
    (permissionCode: string) => (data ?? new Set()).has(permissionCode),
    [data],
  );

  return {
    loading: isLoading,
    error,
    has,
  } as const;
}

export function useOdontogramAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Odontogram (tooth charting) + historical baseline conditions - dental plan only
  return { loading, allowed: has('tooth_notes:read') } as const;
}

export function useDentalChartingAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Check for odontogram (tooth_notes) - dental plan only
  // Deprecated: Use useOdontogramAccess() instead
  return { loading, allowed: has('tooth_notes:read') && has('perio_measurements:read') } as const;
}

export function usePerioChartingAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Perio charting - dental plan only
  return { loading, allowed: has('perio_measurements:read') } as const;
}

export function useClinicalRecordsAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Clinical execution records - basic and dental plans
  return { loading, allowed: has('procedure_episodes:read') } as const;
}

/**
 * Procedure workflow access - basic and dental plans
 * Allows recording procedures without chart features (odontogram/perio)
 * Basic users get procedure recording but no tooth charting
 */
export function useProcedureWorkflowAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Check for procedure recording capability (clinical execution records)
  return { loading, allowed: has('procedure_episodes:read') } as const;
}

export function useProceduresLibraryAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Procedures catalog - available to basic and dental plans
  return { loading, allowed: has('procedures:read') } as const;
}

export function useTreatmentsAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Treatment codes library - dental plan only
  return { loading, allowed: has('treatment_codes:read') } as const;
}

// Standalone function to fetch SSO access (no closure)
async function fetchSSOAccess(): Promise<boolean> {
  const { data: { user: currentUser } } = await supabase.auth.getUser();
  if (!currentUser) return false;

  const { data: userRecord, error } = await supabase
    .from('users')
    .select('sso_access')
    .eq('id', currentUser.id)
    .maybeSingle();

  if (error) throw error;
  return userRecord?.sso_access ?? false;
}

export function useSSOAccess() {
  const { isReady, session } = useAuth();

  const { data, isLoading } = useSupabaseQuery(
    qk.detail('user-sso-access', 'current'),
    async () => fetchSSOAccess(),
    {
      enabled: isReady && !!session,
      namespaces: ['ui', 'auth'],
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  return { loading: isLoading, allowed: data ?? false } as const;
}

export function useMedicalFormsAccess() {
  const { loading, has } = usePermissionEntitlements();
  return { loading, allowed: has('medical_forms:read') } as const;
}

export function usePrescriptionsAccess() {
  const { loading, has } = usePermissionEntitlements();
  return { loading, allowed: has('prescriptions:read') } as const;
}

export function usePatientNotesAccess() {
  const { loading, has } = usePermissionEntitlements();
  // Patient notes - available to basic and dental plans
  return { loading, allowed: has('patient_notes:read') } as const;
}

export function useTeamManagementAccess() {
  const { loading, has } = useRolePermissions();
  // Team member management - requires practice_users:update role permission (Admin role)
  return { loading, allowed: has('practice_users:update') } as const;
}

export function usePracticeEditAccess() {
  const { loading, has } = useRolePermissions();
  // Practice settings edit - Admin, Dentist, or Hygienist can edit practice details
  return { loading, allowed: has('practices:update') } as const;
}

export function usePracticeClosuresAccess() {
  const { loading, has } = useRolePermissions();
  // Practice closures management - Admin or Dentist can manage closures
  return { loading, allowed: has('practice_closures:update') } as const;
}

export function useOrganisationEditAccess() {
  const { loading, has } = useRolePermissions();
  // Organisation management - Admin only
  return { loading, allowed: has('organisations:update') } as const;
}

export function useRoleEditAccess() {
  const { loading, has } = useRolePermissions();
  // Role management - Admin only (create, update, delete roles and assign permissions)
  return { loading, allowed: has('roles:update') } as const;
}

export function useFormTemplateEditAccess() {
  const { loading, has } = useRolePermissions();
  // Medical form template management - Admin only for edit/create/delete
  return { loading, allowed: has('medical_form_templates:update') } as const;
}

export function useOnlineBookingEditAccess() {
  const { loading, has } = useRolePermissions();
  // Online booking settings - Uses practices:update permission (Admin, Dentist, Hygienist)
  return { loading, allowed: has('practices:update') } as const;
}
